import * as React from 'react';
import styled from 'styled-components';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import Label, { LabelAnimateState } from './Label';

/**
 * NOTE: This is pretty much directly copied from nth-block-text-input
 *       I couldn't use nth-block-text-input because for a few reasons:
 *       1) react-hook-form types change handlers as:
 *          `export type ChangeHandler = (event: any) => Promise<void | boolean>;`
 *          nth-block-text-input types change handlers as:
 *          `(event: React.ChangeEvent<HTMLInputElement>) => void
 *          This leads to the error:
 *          Type 'ChangeHandler' is not assignable to type '() => void'
 *       2) I don't know if the error theming works, though I don't the ins and outs
 *          of theming yet, so I could be missing something. Either way, I saw
 *          `inputStyleDefault` populated from the theme query, but `inputStyleError`
 *          came back undefined. So I'm adding defaults as back up
 */

export enum SupportedInputType {
  text = 'text',
  password = 'password',
  email = 'email',
  number = 'number',
  tel = 'tel',
  search = 'search',
}

const centeredStyle: React.CSSProperties = {
  textAlign: 'center',
  paddingLeft: 0,
  paddingRight: 0,
};

const disable = ({
  inputStyleDisabled,
}: {
  inputStyleDisabled?: React.CSSProperties;
}): React.CSSProperties => ({
  borderColor: '#e0e0e0',
  color: '#e0e0e0',
  cursor: 'auto',
  ...inputStyleDisabled,
});

const Container = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const StyledInput = styled.input<{
  s?: {
    sFocus?: React.CSSProperties;
    sDefault?: React.CSSProperties;
    sError?: React.CSSProperties;
    sContainerOverrides?: React.CSSProperties;
    centered?: boolean;
    inputActive?: boolean;
  };
}>(({ s }) => ({
  boxSizing: 'border-box',
  width: '100%',
  borderStyle: 'solid',
  borderColor: '#999',
  borderWidth: '0.0625rem',
  borderRadius: '0.125rem',
  cursor: 'text',
  padding:
    s && s.inputActive
      ? '1.25rem 0.75rem 0.25rem 0.5rem'
      : '0.75rem 0.75rem 0.75rem 0.5rem',
  backgroundColor: 'transparent',
  fontSize: '0.875rem',
  color: '#000',
  textAlign: 'left',
  ...(s && s.sDefault),
  '&:focus': {
    borderColor: '#111',
    outlineWidth: '0',
    outline: 'none',
    ...(s && s.sFocus),
    ...(s && s.sError),
  },
  ...(s && s.centered && centeredStyle),
  ...(s && s.sError),
  ...(s && s.sContainerOverrides),
}));

export interface BaseInputProps {
  id?: string;
  name?: string;
  title?: string;
  type?: SupportedInputType;
  disabled?: boolean;
  centered?: boolean;
  animateLabel?: boolean;
  placeholder?: string;
  label?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  minLength?: number;
  maxLength?: number;
  featureName?: string;
  error?: boolean;
  containerOverridesStyle?: React.CSSProperties;
  ariaDescribedby?: string;
}

const BaseInput = React.forwardRef<HTMLInputElement, BaseInputProps>(
  (
    {
      id,
      centered = false,
      animateLabel = false,
      label,
      name,
      title,
      type = SupportedInputType.text,
      disabled,
      value,
      placeholder,
      onChange,
      onFocus,
      onBlur,
      minLength,
      maxLength,
      featureName,
      error = false,
      containerOverridesStyle, // for styling complex inputs with dropdowns (e.g., telephone input)
      ariaDescribedby,
    },
    ref,
  ) => {
    const [inputActive, setInputActive] = React.useState(Boolean(value));
    const [animateState, setAnimateState] = React.useState(
      LabelAnimateState.none,
    );

    React.useEffect(() => {
      if (!animateLabel) return;
      setAnimateState(
        inputActive ? LabelAnimateState.active : LabelAnimateState.inactive,
      );
    }, [inputActive, animateLabel]);

    const {
      inputtextinputsNoneDisabled: inputStyleDisabled,
      inputtextinputsNoneFocus: inputStyleFocus,
      inputtextinputsNoneNone: inputStyle,
      captiontextErrorNone: captionTextError,
    } = useTheme();

    const errorColor = (captionTextError as { color?: string } | undefined)
      ?.color;

    return (
      <Container>
        {label && (
          <Label
            htmlFor={id}
            labelState={animateState}
            centered={centered}
            featureName={featureName}
          >
            {label}
          </Label>
        )}
        <StyledInput
          ref={ref}
          id={id}
          s={{
            sError:
              error && errorColor ? { border: `1px solid ${errorColor}` } : {},
            sFocus: inputStyleFocus,
            sDefault: disabled ? disable({ inputStyleDisabled }) : inputStyle,
            sContainerOverrides: containerOverridesStyle,
            centered,
            inputActive,
          }}
          name={name}
          title={title}
          type={type}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e): void => {
            if (onChange) onChange(e);
            setInputActive(true);
          }}
          onFocus={(): void => {
            setInputActive(true);
            if (onFocus) onFocus();
          }}
          onBlur={(e): void => {
            setInputActive(Boolean(value));
            if (onBlur) onBlur(e);
          }}
          minLength={minLength}
          maxLength={maxLength}
          aria-invalid={error}
          aria-describedby={ariaDescribedby}
        />
      </Container>
    );
  },
);

export default withThemeProvider(BaseInput, {
  themeTokens: [
    'inputtextinputsNoneDisabled',
    'inputtextinputsNoneFocus',
    'inputtextinputsNoneNone',
    'captiontextErrorNone',
  ],
});
